import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { supabase } from '@/lib/supabaseClient';
import { useToast } from '@/components/ui/use-toast';
import ReportMetricCard from '@/components/reports/ReportMetricCard';
import TasksByStatusChart from '@/components/reports/TasksByStatusChart';
import OverdueTasksList from '@/components/reports/OverdueTasksList';
import { ListChecks, AlertTriangle, Users, Briefcase, BarChart3, Clock } from 'lucide-react';

const ReportsPage = () => {
  const [reportData, setReportData] = useState({
    totalProjects: 0,
    totalTasks: 0,
    completedTasks: 0,
    tasksInProgress: 0,
    totalEmployees: 0,
    tasksByStatus: [],
    overdueTasks: [],
  });
  const [isLoading, setIsLoading] = useState(true);
  const { toast } = useToast();

  useEffect(() => {
    const fetchReportData = async () => {
      setIsLoading(true);
      try {
        const [
          projectsRes,
          tasksRes,
          employeesRes,
        ] = await Promise.all([
          supabase.from('projects').select('id', { count: 'exact' }),
          supabase.from('tasks').select('id, status_id, due_date, custom_statuses(name), projects(name)'),
          supabase.from('employees').select('id', { count: 'exact' }),
        ]);

        if (projectsRes.error) throw new Error(`Projects: ${projectsRes.error.message}`);
        if (tasksRes.error) throw new Error(`Tasks: ${tasksRes.error.message}`);
        if (employeesRes.error) throw new Error(`Employees: ${employeesRes.error.message}`);

        const allTasks = tasksRes.data || [];
        
        const tasksByStatusMap = allTasks.reduce((acc, task) => {
          const statusName = task.custom_statuses?.name || 'Uncategorized';
          acc[statusName] = (acc[statusName] || 0) + 1;
          return acc;
        }, {});

        const tasksByStatusArray = Object.entries(tasksByStatusMap).map(([name, count]) => ({ name, count }));
        
        const now = new Date().toISOString();
        const overdueTasksData = allTasks.filter(task => task.due_date && task.due_date < now && task.custom_statuses?.name !== 'Done');

        setReportData({
          totalProjects: projectsRes.count || 0,
          totalTasks: allTasks.length,
          completedTasks: tasksByStatusMap['Done'] || 0,
          tasksInProgress: tasksByStatusMap['In Progress'] || 0,
          totalEmployees: employeesRes.count || 0,
          tasksByStatus: tasksByStatusArray,
          overdueTasks: overdueTasksData,
        });

      } catch (error) {
        console.error('Error fetching report data:', error);
        toast({
          title: 'Error fetching reports',
          description: `Could not load report data: ${error.message}`,
          variant: 'destructive',
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchReportData();
  }, [toast]);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: 'spring',
        stiffness: 100,
      },
    },
  };

  return (
    <div className="space-y-8 pb-12">
      <motion.h1
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-4xl font-bold tracking-tight text-transparent bg-clip-text bg-gradient-to-r from-indigo-400 to-cyan-400 mb-10"
      >
        Project & Team Insights
      </motion.h1>

      <motion.div 
        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        <motion.div variants={itemVariants}>
          <ReportMetricCard
            title="Total Projects"
            value={reportData.totalProjects}
            icon={Briefcase}
            description="All active and past projects"
            isLoading={isLoading}
            color="text-indigo-400"
          />
        </motion.div>
        <motion.div variants={itemVariants}>
          <ReportMetricCard
            title="Total Tasks"
            value={reportData.totalTasks}
            icon={BarChart3}
            description="Across all projects"
            isLoading={isLoading}
            color="text-sky-400"
          />
        </motion.div>
        <motion.div variants={itemVariants}>
          <ReportMetricCard
            title="Tasks Completed"
            value={reportData.completedTasks}
            icon={ListChecks}
            description="Marked as 'Done'"
            isLoading={isLoading}
            color="text-emerald-400"
          />
        </motion.div>
        <motion.div variants={itemVariants}>
          <ReportMetricCard
            title="Tasks In Progress"
            value={reportData.tasksInProgress}
            icon={Clock}
            description="Currently being worked on"
            isLoading={isLoading}
            color="text-amber-400"
          />
        </motion.div>
        <motion.div variants={itemVariants}>
          <ReportMetricCard
            title="Overdue Tasks"
            value={reportData.overdueTasks.length}
            icon={AlertTriangle}
            description="Past their due date"
            isLoading={isLoading}
            color="text-red-400"
          />
        </motion.div>
        <motion.div variants={itemVariants}>
          <ReportMetricCard
            title="Total Team Members"
            value={reportData.totalEmployees}
            icon={Users}
            description="Active employees"
            isLoading={isLoading}
            color="text-purple-400"
          />
        </motion.div>
      </motion.div>

      <motion.div 
        className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-8"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        <motion.div variants={itemVariants}>
          <TasksByStatusChart data={reportData.tasksByStatus} isLoading={isLoading} />
        </motion.div>
        <motion.div variants={itemVariants}>
          <OverdueTasksList tasks={reportData.overdueTasks} isLoading={isLoading} />
        </motion.div>
      </motion.div>
    </div>
  );
};

export default ReportsPage;